import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveRefresh } from '../../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import {
  ChevronLeft, ChevronRight, Search, Download, Plus, Settings2, Info, GripVertical, ChevronDown,
  Users, UserCog, ListChecks, Building2,
} from 'lucide-react';
import { Button, Heading, Modal, Select } from '../../../components';
import { useAuth } from '../../../context/AuthContext';
import carePlannerService from '../../../services/carehome_manager/carePlannerService';
import PlannerGrid, { VisitCard } from './PlannerGrid';
import PlanCareModal from './PlanCareModal';
import VisitDetailModal from './VisitDetailModal';
import GroupsModal from './GroupsModal';
import {
  CARD, CARD_TONES, COLOUR_KEY, DEFAULT_PERIODS, Segmented, datesFor, addDays, dayLabel, shortDayLabel,
  localToday, exportCsv, errorMessage, plural, groupVisits, visitKey, toVisit,
} from './shared';

/**
 * Care Planner — plan resident care by day and time, and allocate it to the
 * team. Care homes only.
 *
 * Two ways to look at the same care:
 *
 *   Residents         a row per resident, folded into the manager's groups
 *                     (wings, floors). "+" in any future cell plans care for
 *                     that resident at that time.
 *   Staff allocation  a row per carer with their rostered hours shaded in,
 *                     plus a strip of care still needing a carer that can be
 *                     dragged onto somebody's row.
 *
 * Columns are the four times of day, the 24 hours, or the seven days of the
 * week. Each card opens the occurrence: reassign it, change just that date,
 * record it missed, review it, or open the whole plan.
 *
 * What the carer sees: every occurrence allocated to them appears in the
 * app's My Shift tab, where they read the instructions and record the
 * outcome — which comes straight back here.
 *
 * The page refreshes itself every minute, so "Due now" and "Overdue" move
 * with the clock and outcomes recorded on the floor show up without a reload.
 */

const REFRESH_MS = 60 * 1000;

const FILTERS = [
  { value: 'all', label: 'All care' },
  { value: 'unassigned', label: 'Unassigned', count: 'unassigned' },
  { value: 'missed', label: 'Missed', count: 'missed' },
  { value: 'review', label: 'Needs review', count: 'review' },
];

const matchesFilter = (filter) => (t) => {
  if (filter === 'unassigned') return t.needs_allocation;
  if (filter === 'missed') return t.state === 'missed';
  if (filter === 'review') return t.needs_review;
  return true;
};

const Stat = ({ children, tone }) => (
  <span className={`text-[12px] font-semibold ${
    tone === 'purple' ? 'text-purple-700 dark:text-purple-300'
      : tone === 'blue' ? 'text-blue-600 dark:text-blue-300'
      : tone === 'amber' ? 'text-amber-700 dark:text-amber-400'
        : tone === 'red' ? 'text-rose-600 dark:text-rose-400'
          : tone === 'green' ? 'text-emerald-700 dark:text-emerald-400'
            : 'text-[#64748B] dark:text-gray-400'
  }`}
  >
    {children}
  </span>
);

const CarePlanner = () => {
  const { user } = useAuth();
  const isDomiciliary = (user?.organization?.organization_type || '').toLowerCase() === 'domiciliary';

  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [data, setData] = useState({ tasks: [], rota: {}, today: null });
  const [loadingTasks, setLoadingTasks] = useState(true);

  const [view, setView] = useState('day');
  const [date, setDate] = useState(localToday());
  const [mode, setMode] = useState('resident');
  const [filter, setFilter] = useState('all');
  const [hourly, setHourly] = useState(false);
  const [groupFilter, setGroupFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [queueOpen, setQueueOpen] = useState(true);

  const [plan, setPlan] = useState(null);       // { kind: 'create', preset } | { kind: 'edit', planId, back? }
  const [detail, setDetail] = useState(null);   // { key, preset? } — a care visit | { back: { planId, date } }
  const [groups, setGroups] = useState(null);   // { kind: 'edit', group? } | { kind: 'manage' }
  const [cell, setCell] = useState(null);       // { title, subtitle, visits }
  const [keyOpen, setKeyOpen] = useState(false);

  const today = data.today || meta?.today || localToday();
  const dates = useMemo(() => datesFor(view, date), [view, date]);
  const from = dates[0];
  const to = dates[dates.length - 1];

  /* -------------------------------------------------------- loading - */

  const loadMeta = useCallback(async () => {
    try {
      const res = await carePlannerService.getMeta();
      setMeta(res.data);
      setMetaError('');
    } catch (err) {
      setMetaError(errorMessage(err, 'Could not load the care planner.'));
    }
  }, []);

  const loadTasks = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoadingTasks(true);
    try {
      const res = await carePlannerService.getTasks(from, to);
      setData(res.data);
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load planned care.'));
    } finally {
      if (!quiet) setLoadingTasks(false);
    }
  }, [from, to]);

  useEffect(() => {
    if (!isDomiciliary) loadMeta();
  }, [isDomiciliary, loadMeta]);

  useEffect(() => {
    if (isDomiciliary || metaError) return undefined;
    loadTasks();
    const timer = setInterval(() => loadTasks({ quiet: true }), REFRESH_MS);
    return () => clearInterval(timer);
  }, [isDomiciliary, metaError, loadTasks]);
  // A shift clocked, an agency booking confirmed or cancelled — the carers'
  // rota and their allocated care follow at once, not at the next tick.
  useLiveRefresh(['rota', 'agency'], () => { if (!isDomiciliary && !metaError) loadTasks({ quiet: true }); });

  // The open visit is always read from the latest tasks, so a background
  // refresh — or a task added to it by "Edit planned care" — shows at once.
  const openVisit = useMemo(() => {
    if (!detail) return null;
    // Back from "Edit planned care": that plan's visit on the same date,
    // wherever the edit moved its time to.
    const key = detail.back
      ? data.tasks
        .filter((t) => t.plan_id === detail.back.planId && t.task_date === detail.back.date)
        .map(visitKey).sort()[0]
      : detail.key;
    const tasks = key ? data.tasks.filter((t) => visitKey(t) === key) : [];
    return tasks.length ? toVisit(key, tasks) : null;
  }, [detail, data.tasks]);

  /* ------------------------------------------------------- deriving - */

  const group = (meta?.groups || []).find((g) => String(g.id) === String(groupFilter)) || null;
  const q = search.trim().toLowerCase();

  // Residents and carers named on a task but no longer current (discharged,
  // archived) still get a row, so their care in this period is not hidden.
  const residentList = useMemo(() => {
    const list = [...(meta?.residents || [])];
    const known = new Set(list.map((r) => r.id));
    data.tasks.forEach((t) => {
      if (t.resident && !known.has(Number(t.residence_id))) {
        known.add(Number(t.residence_id));
        list.push({ ...t.resident, id: Number(t.residence_id) });
      }
    });
    return list;
  }, [meta, data.tasks]);

  const carerList = useMemo(() => {
    const list = [...(meta?.carers || [])];
    const known = new Set(list.map((c) => c.id));
    data.tasks.forEach((t) => {
      [[t.assigned_to, t.assigned_name], [t.second_assignee, t.second_name]].forEach(([id, name]) => {
        if (id && !known.has(Number(id))) {
          known.add(Number(id));
          list.push({ id: Number(id), name: name || 'Former staff member', job_title: 'No longer a carer here' });
        }
      });
    });
    return list;
  }, [meta, data.tasks]);

  const scoped = useMemo(() => data.tasks.filter((t) => {
    if (group && !(group.residence_ids || []).map(Number).includes(Number(t.residence_id))) return false;
    if (!q) return true;
    return [t.resident?.name, t.assigned_name, t.second_name, t.action_label, t.category]
      .filter(Boolean).join(' ').toLowerCase().includes(q);
  }), [data.tasks, group, q]);

  const counts = useMemo(() => ({
    unassigned: scoped.filter((t) => t.needs_allocation).length,
    missed: scoped.filter((t) => t.state === 'missed').length,
    review: scoped.filter((t) => t.needs_review).length,
    overdue: scoped.filter((t) => t.overdue).length,
    dueNow: scoped.filter((t) => t.due_now).length,
    completed: scoped.filter((t) => t.state === 'completed' || t.state === 'not_done').length,
  }), [scoped]);

  const shown = useMemo(() => scoped.filter(matchesFilter(filter)), [scoped, filter]);
  const narrowed = filter !== 'all';

  const sections = useMemo(() => {
    if (mode === 'staff') {
      const rows = carerList
        .filter((c) => !q || c.name.toLowerCase().includes(q) || shown.some((t) => [t.assigned_to, t.second_assignee].map(Number).includes(c.id)))
        .filter((c) => !['missed', 'review'].includes(filter) || shown.some((t) => [t.assigned_to, t.second_assignee].map(Number).includes(c.id)))
        .map((c) => ({ id: c.id, name: c.name, sub: c.job_title, photo: c.avatar_url }));
      return [{ key: 'staff', group: null, rows }];
    }

    const residentRow = (r) => ({ id: r.id, name: r.name, sub: r.room ? `Room ${r.room}` : '', photo: r.photo });
    const rows = residentList
      .filter((r) => !group || (group.residence_ids || []).map(Number).includes(r.id))
      .filter((r) => !q || r.name.toLowerCase().includes(q) || String(r.room || '').toLowerCase().includes(q) || shown.some((t) => Number(t.residence_id) === r.id))
      .filter((r) => !narrowed || shown.some((t) => Number(t.residence_id) === r.id));

    const allGroups = meta?.groups || [];
    if (group) return [{ key: `g${group.id}`, group, rows: rows.map(residentRow) }];
    if (!allGroups.length) return [{ key: 'all', group: null, rows: rows.map(residentRow) }];

    // Each resident sits under the first group that holds them; everyone
    // else under "Other residents".
    const placed = new Set();
    const out = allGroups.map((g) => {
      const ids = new Set((g.residence_ids || []).map(Number));
      const members = rows.filter((r) => ids.has(r.id) && !placed.has(r.id));
      members.forEach((r) => placed.add(r.id));
      return { key: `g${g.id}`, group: g, rows: members.map(residentRow) };
    }).filter((s) => s.rows.length);
    const rest = rows.filter((r) => !placed.has(r.id));
    if (rest.length) out.push({ key: 'other', group: { id: null, name: 'Other residents', color: '#94A3B8' }, rows: rest.map(residentRow) });
    return out;
  }, [mode, carerList, residentList, q, shown, filter, narrowed, group, meta]);

  const rowCount = sections.reduce((n, s) => n + s.rows.length, 0);
  const queue = useMemo(() => (mode === 'staff' && !['missed', 'review'].includes(filter)
    ? groupVisits(shown.filter((t) => t.needs_allocation))
      .sort((a, b) => `${a.first.task_date}${a.first.start_time}`.localeCompare(`${b.first.task_date}${b.first.start_time}`))
    : []), [mode, filter, shown]);

  /* ------------------------------------------------------- handlers - */

  const step = (dir) => setDate((d) => addDays(d, dir * (view === 'week' ? 7 : 1)));
  const showsToday = dates.includes(today);

  const replaceTasks = (updated) => {
    const byId = new Map(updated.map((t) => [t.id, t]));
    setData((d) => ({ ...d, tasks: d.tasks.map((t) => byId.get(t.id) || t) }));
    // A time change moves the visit; follow it so the panel stays open.
    if (updated[0]) setDetail((d) => (d ? { key: visitKey(updated[0]) } : d));
  };

  const openDrop = (key, row) => {
    const tasks = data.tasks.filter((t) => visitKey(t) === key);
    const task = tasks.find((t) => t.open);
    if (!task) {
      toast.error('This care has already been recorded.');
      return;
    }
    const preset = task.staff_required === 2 && task.assigned_to && !task.second_assignee && Number(task.assigned_to) !== row.id
      ? { secondId: row.id }
      : { carerId: row.id };
    setDetail({ key, preset });
  };

  const planSaved = (message, firstDate) => {
    toast.success(message);
    // Back to the visit the edit was opened from, showing what changed.
    if (plan?.back) setDetail({ back: plan.back });
    setPlan(null);
    if (firstDate && !dates.includes(firstDate)) setDate(firstDate);
    else loadTasks({ quiet: true });
  };

  const exportShown = () => {
    if (!shown.length) {
      toast.error('There is no care in this view to export.');
      return;
    }
    exportCsv(shown, `care-planner-${from}${to !== from ? `-to-${to}` : ''}.csv`);
  };

  /* --------------------------------------------------------- render - */

  if (isDomiciliary || /care homes/i.test(metaError)) {
    return (
      <div className={`${CARD} flex flex-col items-center text-center py-20 px-6`}>
        <span className="w-12 h-12 rounded-2xl bg-[#F1F5F9] dark:bg-gray-800 grid place-items-center mb-4">
          <Building2 className="w-6 h-6 text-[#94A3B8]" />
        </span>
        <h1 className="text-[17px] font-bold text-[#0B1220] dark:text-gray-100">The Care Planner is for care homes</h1>
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5 max-w-md">Domiciliary visits are planned in your Rota.</p>
      </div>
    );
  }

  if (metaError) {
    return (
      <div className={`${CARD} flex flex-col items-center text-center py-20 px-6`}>
        <h1 className="text-[17px] font-bold text-[#0B1220] dark:text-gray-100">The Care Planner could not be opened</h1>
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5 max-w-md">{metaError}</p>
        <Button className="mt-5" variant="secondary" onClick={loadMeta}>Try again</Button>
      </div>
    );
  }

  const periodLabel = view === 'week' ? `Week of ${shortDayLabel(from)} – ${shortDayLabel(to)}` : dayLabel(date);
  const boardTitle = filter === 'missed' ? 'Missed care'
    : filter === 'review' ? 'Needs review'
      : filter === 'unassigned' ? 'Care needing allocation'
        : mode === 'resident' ? 'Resident care schedule' : 'Staff allocation';
  const boardHint = filter === 'missed' ? 'Confirmed missed care, with its recorded reason.'
    : filter === 'review' ? 'Missed care, late care and care recorded as not done, awaiting your review.'
      : mode === 'resident' ? 'Click a card to allocate or review it, or + to plan care for that resident.'
        : 'Drag unassigned care onto a carer’s row, then confirm the allocation.';

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-col 2xl:flex-row 2xl:items-center justify-between gap-4">
        <div className="flex items-center gap-x-5 gap-y-3 flex-wrap">
          <Heading level={1}>Care Planner</Heading>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" icon={<ChevronLeft className="w-4 h-4" />} aria-label="Previous period" onClick={() => step(-1)} />
            <span className="min-w-[150px] text-center text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 tabular-nums">{periodLabel}</span>
            <Button size="sm" variant="ghost" icon={<ChevronRight className="w-4 h-4" />} aria-label="Next period" onClick={() => step(1)} />
            {!showsToday && <Button size="sm" variant="soft" onClick={() => setDate(today)}>Today</Button>}
            <input
              type="date"
              aria-label="Go to date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="h-[34px] ml-1 px-2 rounded-[10px] border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33] text-[12.5px] text-[#334155] dark:text-gray-200"
            />
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <label className="flex items-center gap-2 h-10 px-3 w-full sm:w-[260px] rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33]">
            <Search className="w-4 h-4 text-[#94A3B8] shrink-0" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search resident, staff or task…"
              aria-label="Search the schedule"
              className="flex-1 min-w-0 bg-transparent text-[13px] text-[#0B1220] dark:text-gray-100 placeholder-[#A0AEC0] focus:outline-none"
            />
          </label>
          <Segmented label="Period" value={view} onChange={setView} options={[{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }]} />
          <Button variant="secondary" leftIcon={<Download className="w-4 h-4" />} onClick={exportShown}>Export</Button>
        </div>
      </div>

      {/* ── Summary ── */}
      <section className={`${CARD} px-5 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4`}>
        <div>
          <h2 className="text-[16.5px] font-bold text-[#0B1220] dark:text-gray-100">
            {view === 'week' ? 'Weekly' : 'Daily'} {mode === 'resident' ? 'resident schedule' : 'staff allocation'}
          </h2>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 [&>*+*]:border-l [&>*+*]:border-[#E3E9F3] dark:[&>*+*]:border-gray-700 [&>*+*]:pl-3">
            <Stat>{plural((meta?.groups || []).length, 'group')}</Stat>
            <Stat>{plural(rowCount, mode === 'resident' ? 'resident' : 'carer')}</Stat>
            <Stat>{plural(shown.length, 'care action')}</Stat>
            {counts.dueNow > 0 && <Stat tone="blue">{counts.dueNow} due now</Stat>}
            {counts.unassigned > 0 && <Stat tone="purple">{counts.unassigned} unassigned</Stat>}
            {counts.overdue > 0 && <Stat tone="amber">{counts.overdue} overdue</Stat>}
            {counts.missed > 0 && <Stat tone="red">{counts.missed} missed</Stat>}
            {counts.completed > 0 && <Stat tone="green">{counts.completed} recorded</Stat>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setPlan({ kind: 'create', preset: { date: from >= today ? from : today } })} disabled={!meta}>
            Add planned care
          </Button>
          <Button variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setGroups({ kind: 'edit', group: null })} disabled={!meta}>Add group</Button>
          <Button variant="secondary" leftIcon={<Settings2 className="w-4 h-4" />} onClick={() => setGroups({ kind: 'manage' })} disabled={!meta}>Manage groups</Button>
          <Button variant="ghost" leftIcon={<Info className="w-4 h-4" />} onClick={() => setKeyOpen(true)}>Colour key</Button>
        </div>
      </section>

      {/* ── Controls ── */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            label="Schedule view"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'resident', label: 'Residents', icon: <Users className="w-4 h-4" /> },
              { value: 'staff', label: 'Staff allocation', icon: <UserCog className="w-4 h-4" /> },
            ]}
          />
          <Select
            value={String(groupFilter)}
            onChange={setGroupFilter}
            options={[{ value: 'all', label: 'All groups' }, ...(meta?.groups || []).map((g) => ({ value: String(g.id), label: g.name }))]}
            size="sm"
            className="w-[190px]"
          />
          {view === 'day' && (
            <button
              type="button"
              role="switch"
              aria-checked={hourly}
              onClick={() => setHourly((h) => !h)}
              className="inline-flex items-center gap-2.5 h-[34px] px-3 rounded-[10px] border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33] text-[12.5px] font-semibold text-[#334155] dark:text-gray-200"
            >
              Hourly
              <span className={`relative w-9 h-5 rounded-full transition-colors ${hourly ? 'bg-blue-600' : 'bg-[#CBD5E1] dark:bg-gray-600'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${hourly ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Show">
          <span className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mr-1">Show</span>
          {FILTERS.map((f) => {
            const active = filter === f.value;
            const n = f.count ? counts[f.count] : null;
            return (
              <button
                key={f.value}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f.value)}
                className={`inline-flex items-center gap-2 h-[34px] px-3.5 rounded-full border text-[12.5px] font-semibold transition-colors ${
                  active
                    ? 'bg-[#EEF3FF] border-blue-400 text-blue-700 dark:bg-blue-900/25 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-[#262b33] border-[#E3E9F3] dark:border-gray-700 text-[#334155] dark:text-gray-300 hover:border-blue-300'
                }`}
              >
                {f.label}
                {n !== null && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-bold ${
                    !n ? 'bg-[#F1F5F9] text-[#94A3B8] dark:bg-gray-800'
                      : f.value === 'unassigned' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                        : f.value === 'missed' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Unassigned queue (staff allocation) ── */}
      {mode === 'staff' && !['missed', 'review'].includes(filter) && (
        <section className="rounded-[16px] border border-[#EDE3FF] dark:border-purple-900/40 bg-[#F8F4FF] dark:bg-purple-950/20 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setQueueOpen((o) => !o)} aria-expanded={queueOpen} className="inline-flex items-center gap-2 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
              {queueOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Unassigned care
              <span className="min-w-[22px] h-[22px] px-1.5 rounded-full grid place-items-center bg-purple-200/70 dark:bg-purple-900/50 text-[11.5px]">{queue.length}</span>
            </button>
            <span className="text-[12px] text-purple-600/80 dark:text-purple-300/70">Click to assign · Drag onto a carer</span>
          </div>
          {queueOpen && (
            queue.length ? (
              <div className="mt-3 grid gap-2 grid-cols-[repeat(auto-fill,minmax(230px,1fr))]">
                {queue.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', v.key); e.dataTransfer.effectAllowed = 'move'; }}
                    onClick={() => setDetail({ key: v.key })}
                    className="grid grid-cols-[16px_1fr] gap-x-2 text-left rounded-[10px] border border-[#DFCFFF] dark:border-purple-900/50 bg-white dark:bg-[#262b33] px-3 py-2.5 cursor-grab active:cursor-grabbing hover:border-purple-400"
                  >
                    <GripVertical className="w-4 h-4 text-purple-300 row-span-3 self-center" />
                    <strong className="text-[12.5px] text-purple-700 dark:text-purple-300 truncate">{v.first.resident?.name}</strong>
                    <span className="text-[12px] text-[#334155] dark:text-gray-300 truncate" title={v.labels.join(', ')}>
                      {v.labels.join(', ')}{v.first.staff_required === 2 ? ' · 2 staff' : ''}
                    </span>
                    <span className="text-[11px] text-[#94A3B8] tabular-nums">{dayLabel(v.first.task_date)} · {v.first.start_time}–{v.first.end_time}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-[12.5px] text-purple-700/80 dark:text-purple-300/70">
                {shown.length ? 'All care in this period has a named carer.' : 'No care scheduled in this period.'}
              </p>
            )
          )}
        </section>
      )}

      {/* ── Board ── */}
      <section className={`${CARD} overflow-hidden`}>
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-[#EDF1F7] dark:border-gray-800">
          <div>
            <h2 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">{boardTitle}</h2>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{boardHint}</p>
          </div>
          <span className="shrink-0 text-[11.5px] font-bold text-[#334155] dark:text-gray-300 bg-[#F1F5F9] dark:bg-gray-800 rounded-full px-3 py-1.5">
            {shown.length} care action{shown.length === 1 ? '' : 's'}
          </span>
        </header>

        {!meta || loadingTasks ? (
          <div className="p-5 space-y-3 animate-pulse" aria-label="Loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <div className="w-[140px] h-20 rounded-xl bg-[#EEF2F7] dark:bg-gray-800" />
                <div className="flex-1 h-20 rounded-xl bg-[#F3F6FA] dark:bg-gray-800/70" />
              </div>
            ))}
          </div>
        ) : rowCount === 0 ? (
          <div className="flex flex-col items-center text-center py-16 px-6">
            <span className="w-12 h-12 rounded-2xl bg-[#F1F5F9] dark:bg-gray-800 grid place-items-center mb-4">
              <ListChecks className="w-6 h-6 text-[#94A3B8]" />
            </span>
            <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">
              {narrowed
                ? `No ${filter === 'review' ? 'care awaiting review' : filter === 'missed' ? 'missed care' : 'unassigned care'} in this period`
                : q ? 'Nothing matches your search'
                  : mode === 'resident' ? 'No residents yet' : 'No carers to allocate'}
            </h3>
            <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5 max-w-sm">
              {narrowed || q
                ? 'Clear the filter or search for another resident, carer or task.'
                : mode === 'resident' ? 'Add residents first — care is planned resident by resident.' : 'Carers added under Staff appear here to allocate care to.'}
            </p>
            {(narrowed || q) && <Button className="mt-5" variant="secondary" onClick={() => { setFilter('all'); setSearch(''); }}>Clear filters</Button>}
          </div>
        ) : (
          <PlannerGrid
            mode={mode}
            view={view}
            hourly={hourly && view === 'day'}
            dates={dates}
            sections={sections}
            tasks={shown}
            periods={meta.periods || DEFAULT_PERIODS}
            rota={data.rota}
            today={today}
            canAdd={filter === 'all' || filter === 'unassigned'}
            collapsed={collapsed}
            onToggleGroup={(key) => setCollapsed((s) => {
              const next = new Set(s);
              if (next.has(key)) next.delete(key); else next.add(key);
              return next;
            })}
            onEditGroup={(g) => setGroups({ kind: 'edit', group: g })}
            onAdd={(row, d, time) => setPlan({ kind: 'create', preset: { residenceId: row.id, date: d, time } })}
            onOpenVisit={(visit) => setDetail({ key: visit.key })}
            onOpenDay={(d) => { setDate(d); setView('day'); }}
            onDropTask={openDrop}
            onViewAll={(row, column, items) => setCell({
              title: row.name,
              subtitle: `${view === 'week' ? dayLabel(column.date) : `${dayLabel(column.date)} · ${column.label}`} · ${plural(items.length, 'visit')}`,
              visits: items,
            })}
          />
        )}
      </section>

      {/* ── Dialogs ── */}
      {plan && meta && (
        <PlanCareModal
          request={plan}
          meta={meta}
          rota={data.rota}
          today={today}
          onClose={() => {
            if (plan.back) setDetail({ back: plan.back });
            setPlan(null);
          }}
          onSaved={planSaved}
        />
      )}

      {openVisit && meta && (
        <VisitDetailModal
          key={openVisit.key}
          visit={openVisit}
          preset={detail.preset}
          meta={meta}
          rota={data.rota}
          onClose={() => setDetail(null)}
          onChanged={replaceTasks}
          onRemoved={() => { setDetail(null); loadTasks({ quiet: true }); }}
          onEditPlan={(planId) => {
            setDetail(null);
            setPlan({ kind: 'edit', planId, back: { planId, date: openVisit.first.task_date } });
          }}
        />
      )}

      {groups && meta && (
        <GroupsModal
          request={groups}
          meta={meta}
          onClose={() => setGroups(null)}
          onChanged={loadMeta}
          onEdit={(g) => setGroups({ kind: 'edit', group: g })}
        />
      )}

      <Modal isOpen={!!cell} onClose={() => setCell(null)} title={cell ? `${cell.title} · ${cell.subtitle}` : ''} maxWidth="max-w-md" closeOnBackdrop>
        <div className="space-y-2">
          {cell?.visits.map((v) => (
            <VisitCard key={v.key} visit={v} mode={mode} onOpen={(visit) => { setCell(null); setDetail({ key: visit.key }); }} />
          ))}
        </div>
      </Modal>

      <Modal isOpen={keyOpen} onClose={() => setKeyOpen(false)} title="Colour key" maxWidth="max-w-md" closeOnBackdrop>
        <ul className="space-y-3">
          {COLOUR_KEY.map((k) => (
            <li key={k.tone} className="flex items-start gap-3">
              <span className={`w-12 h-8 shrink-0 rounded-md border border-l-[4px] ${CARD_TONES[k.tone]}`} />
              <span>
                <span className="block text-[13px] font-bold text-[#0B1220] dark:text-gray-100">{k.label}</span>
                <span className="block text-[12px] text-[#64748B] dark:text-gray-400">{k.text}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[12px] text-[#64748B] dark:text-gray-400">
          A red dot marks urgent care and an amber dot important care. “Needs review” flags missed, late and not-done care until a manager reviews it.
        </p>
      </Modal>
    </div>
  );
};

export default CarePlanner;
